import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IoClose } from "react-icons/io5";

const apiURL = process.env.REACT_APP_API_URL;

const formatRemaining = (ms) => {
  if (ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

const hexToRGBA = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(255,255,255,${alpha})`;
  let c = hex.replace("#", "");
  if (c.length === 3) {
    c = c.split("").map((ch) => ch + ch).join("");
  }
  const bigint = parseInt(c, 16);
  if (Number.isNaN(bigint)) return `rgba(255,255,255,${alpha})`;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TopBanner = () => {
  const [config, setConfig] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const textColor = useMemo(() => (config?.textColor || "#ffffff"), [config]);
  const chipBg = useMemo(() => hexToRGBA(textColor, 0.15), [textColor]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${apiURL}/api/banner/current`);
        if (res.data && res.data.banner) setConfig(res.data.banner);
      } catch (err) {
        console.error("Banner fetch error:", err);
      }
    };
    fetchConfig();
  }, []);

  // Respect user's hide only for the same banner version (updatedAt)
  useEffect(() => {
    if (!config) return;
    const params = new URLSearchParams(window.location.search);
    const debug = params.get("bannerDebug") === "1";
    const showOverride = params.get("showBanner") === "1";
    if (showOverride) {
      setHidden(false);
      if (debug) console.info("[TopBanner] Override via showBanner=1");
      return;
    }

    const version = config.updatedAt ? new Date(config.updatedAt).toISOString() : "v1";
    const storedRaw = sessionStorage.getItem("hideTopBannerVersion");
    let shouldHide = false;
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        const ts = typeof stored.ts === "number" ? stored.ts : 0;
        const maxAgeMs = 3 * 60 * 60 * 1000; // 3h TTL
        const fresh = Date.now() - ts < maxAgeMs;
        shouldHide = stored.v === version && fresh;
      } catch (_) {
        // ignore parse errors
      }
    }
    setHidden(shouldHide);
    if (debug) console.info("[TopBanner] version=", version, " hidden=", shouldHide);
  }, [config]);

  useEffect(() => {
    if (!config || !config.endAt) {
      setRemaining(null);
      return;
    }
    const end = new Date(config.endAt).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = end - now;
      setRemaining(formatRemaining(diff));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [config]);

  if (!config || hidden) return null;
  const isExpired = config.endAt && new Date(config.endAt).getTime() <= Date.now();
  const shouldShow = config.enabled && !isExpired;
  if (!shouldShow) return null;

  return (
    <div
      className="w-full"
      style={{ backgroundColor: config.bgColor || "#1e40af", color: textColor }}
    >
      <div
        className="max-w-6xl mx-auto px-4 py-2 text-sm flex items-center justify-between gap-3"
        style={{ color: textColor }}
      >
        <div className="flex-1">
          <span className="font-semibold" style={{ color: textColor }}>
            {config.text}
          </span>
          {remaining && (
            <span className="ml-3 inline-flex items-center gap-2" style={{ color: textColor }}>
              <span className="px-2 py-0.5 rounded" style={{ background: chipBg }}>{remaining.days}j</span>
              <span className="px-2 py-0.5 rounded" style={{ background: chipBg }}>{remaining.hours}h</span>
              <span className="px-2 py-0.5 rounded" style={{ background: chipBg }}>{remaining.minutes}m</span>
              <span className="px-2 py-0.5 rounded" style={{ background: chipBg }}>{remaining.seconds}s</span>
            </span>
          )}
        </div>
        <button
          aria-label="Fermer la bannière"
          className="p-1 rounded"
          style={{ color: textColor, background: chipBg }}
          onClick={() => {
            setHidden(true);
            const version = config?.updatedAt ? new Date(config.updatedAt).toISOString() : "v1";
            const data = { v: version, ts: Date.now() };
            sessionStorage.setItem("hideTopBannerVersion", JSON.stringify(data));
          }}
        >
          <IoClose className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default TopBanner;
